import { useEffect, useMemo, useState } from 'react'
import { Info, X } from 'lucide-react'
import { saveShmpCourseWeeklyPlan } from '@/services/shmpService'
import type { ShmpCourseNumber, ShmpCourseWeeklyPlanRow } from '@/types'

interface Props {
  isOpen: boolean
  onClose: () => void
  periodId: number | null
  rows: ShmpCourseWeeklyPlanRow[]
  initialWeekNumber?: number | null
  onSaved?: () => Promise<void> | void
}

type WeekState = 'past' | 'current' | 'future'

interface DraftValue {
  plannedStudioCount?: number
  plannedRevenuePerStudioRub?: number
  autoPrefilledStudioCount?: boolean
}

function getTodayIso() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatShortDate(date: string) {
  const [year, month, day] = date.split('-')
  return `${day}.${month}.${year.slice(-2)}`
}

function formatNumberWithSpaces(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) return ''
  return Math.round(value).toLocaleString('ru-RU')
}

function formatCurrencyValue(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('ru-RU')
}

function formatHintValue(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('ru-RU')
}

function parseFormattedNumber(value: string) {
  const digitsOnly = value.replace(/[^\d]/g, '')
  if (!digitsOnly) return undefined
  return Number(digitsOnly)
}

function getRowKey(weekId: number, courseNumber: ShmpCourseNumber) {
  return `${weekId}-${courseNumber}`
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  if (error && typeof error === 'object') {
    const maybeMessage = 'message' in error ? error.message : null
    const maybeDetails = 'details' in error ? error.details : null
    const maybeHint = 'hint' in error ? error.hint : null

    const parts = [maybeMessage, maybeDetails, maybeHint]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)

    if (parts.length > 0) {
      return parts.join(' ')
    }
  }

  return 'Ошибка сохранения плана ШМП'
}

export function ShmpCourseWeeklyPlanModal({
  isOpen,
  onClose,
  periodId,
  rows,
  initialWeekNumber,
  onSaved,
}: Props) {
  const [draftByKey, setDraftByKey] = useState<Record<string, DraftValue>>({})
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const todayIso = useMemo(() => getTodayIso(), [])

  const weeks = useMemo(() => {
    const weekMap = new Map<number, {
      week_id: number
      week_number_in_period: number
      week_start: string
      week_end: string
      rows: ShmpCourseWeeklyPlanRow[]
    }>()

    rows.forEach((row) => {
      const existing = weekMap.get(row.week_id)
      if (existing) {
        existing.rows.push(row)
        return
      }

      weekMap.set(row.week_id, {
        week_id: row.week_id,
        week_number_in_period: row.week_number_in_period,
        week_start: row.week_start,
        week_end: row.week_end,
        rows: [row],
      })
    })

    return [...weekMap.values()].sort((a, b) => a.week_number_in_period - b.week_number_in_period)
  }, [rows])

  useEffect(() => {
    if (!isOpen) return

    const nextDrafts: Record<string, DraftValue> = {}
    rows.forEach((row) => {
      nextDrafts[getRowKey(row.week_id, row.course_number)] = {
        plannedStudioCount: row.planned_studio_count === null
          ? (row.current_studio_count ?? 0)
          : row.planned_studio_count,
        plannedRevenuePerStudioRub: row.planned_revenue_per_studio_rub === null
          ? undefined
          : Number(row.planned_revenue_per_studio_rub),
        autoPrefilledStudioCount: row.planned_studio_count === null,
      }
    })
    setDraftByKey(nextDrafts)

    const currentWeek = weeks.find((week) => week.week_start <= todayIso && week.week_end >= todayIso)
    const requestedWeek = initialWeekNumber === null || initialWeekNumber === undefined
      ? null
      : weeks.find((week) => week.week_number_in_period === initialWeekNumber)

    setSelectedWeekId(requestedWeek?.week_id ?? currentWeek?.week_id ?? weeks[0]?.week_id ?? null)
    setError(null)
  }, [initialWeekNumber, isOpen, rows, todayIso, weeks])

  if (!isOpen) return null

  const getWeekState = (week: { week_start: string, week_end: string }): WeekState => {
    if (week.week_end < todayIso) return 'past'
    if (week.week_start > todayIso) return 'future'
    return 'current'
  }

  const selectedWeek = weeks.find((week) => week.week_id === selectedWeekId) ?? weeks[0] ?? null
  const selectedWeekTotal = selectedWeek?.rows.reduce((sum, row) => {
    const draft = draftByKey[getRowKey(row.week_id, row.course_number)] ?? {}
    if (
      draft.plannedStudioCount === undefined
      || draft.plannedRevenuePerStudioRub === undefined
    ) {
      return sum
    }
    return sum + draft.plannedStudioCount * draft.plannedRevenuePerStudioRub
  }, 0) ?? 0
  const previousPeriodHintTitle = 'Факт/план прошлого периода'

  const handleSave = async () => {
    if (!periodId) {
      setError('Не выбран период для сохранения')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const editableRows = rows.filter((row) => getWeekState(row) !== 'past')
      const items = editableRows.map((row) => {
        const draft = draftByKey[getRowKey(row.week_id, row.course_number)] ?? {}
        const hasRevenue = draft.plannedRevenuePerStudioRub !== undefined
          && draft.plannedRevenuePerStudioRub !== null
        const hasStudioCount = draft.plannedStudioCount !== undefined
          && draft.plannedStudioCount !== null
        const shouldTreatAutoPrefillAsEmpty = !hasRevenue && draft.autoPrefilledStudioCount === true

        return {
          week_id: row.week_id,
          course_number: row.course_number,
          planned_studio_count: shouldTreatAutoPrefillAsEmpty
            ? null
            : (hasStudioCount ? draft.plannedStudioCount ?? null : null),
          planned_revenue_per_studio_rub: hasRevenue ? draft.plannedRevenuePerStudioRub ?? null : null,
        }
      })

      await saveShmpCourseWeeklyPlan({
        periodId,
        items,
      })

      await onSaved?.()
      onClose()
    } catch (err) {
      console.error('Ошибка сохранения плана ШМП:', err)
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Ручной план ШМП по неделям</h2>
            <p className="mt-1 text-sm text-slate-500">
              Для каждого курса задайте количество студий и выручку на одну студию. Прошедшие недели доступны только для просмотра.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-152px)] overflow-y-auto px-5 py-4">
          {selectedWeek && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 md:grid-cols-[minmax(0,1fr)_260px_180px] md:items-end">
                <div>
                  <div className="text-xs font-medium text-slate-500">Неделя</div>
                  <select
                    value={selectedWeek.week_id}
                    onChange={(event) => setSelectedWeekId(Number(event.target.value))}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-card px-3 py-2.5 text-sm text-slate-900"
                  >
                    {weeks.map((week) => (
                      <option key={week.week_id} value={week.week_id}>
                        {`Неделя ${week.week_number_in_period} · ${formatShortDate(week.week_start)} - ${formatShortDate(week.week_end)}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs font-medium text-slate-500">Статус</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        getWeekState(selectedWeek) === 'past'
                          ? 'bg-slate-200 text-slate-600'
                          : getWeekState(selectedWeek) === 'current'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {getWeekState(selectedWeek) === 'past'
                        ? 'Завершена'
                        : getWeekState(selectedWeek) === 'current'
                          ? 'Текущая'
                          : 'Будущая'}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-slate-500">Итого за неделю</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-950">
                    {`${formatCurrencyValue(selectedWeekTotal)} ₽`}
                  </div>
                </div>
              </div>

              <div className="grid gap-3">
                {selectedWeek.rows
                  .sort((a, b) => a.course_number - b.course_number)
                  .map((row) => {
                    const rowKey = getRowKey(row.week_id, row.course_number)
                    const draft = draftByKey[rowKey] ?? {}
                    const plannedRevenue = draft.plannedStudioCount !== undefined
                      && draft.plannedRevenuePerStudioRub !== undefined
                      ? draft.plannedStudioCount * draft.plannedRevenuePerStudioRub
                      : null
                    const isPast = getWeekState(selectedWeek) === 'past'

                    return (
                      <div
                        key={rowKey}
                        className="rounded-xl border border-slate-200 bg-card px-4 py-4"
                      >
                        <div className="grid gap-4 lg:grid-cols-[120px_1fr_1fr_180px] lg:items-start">
                          <div className="pt-8 text-sm font-semibold text-slate-950">
                            {row.course_number} курс
                          </div>

                          <label className="space-y-2">
                            <div className="text-xs font-medium text-slate-700">Студий в плане</div>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={formatNumberWithSpaces(draft.plannedStudioCount)}
                              onChange={(event) => {
                                const value = parseFormattedNumber(event.target.value)
                                setDraftByKey((prev) => ({
                                  ...prev,
                                  [rowKey]: {
                                    ...prev[rowKey],
                                    plannedStudioCount: value,
                                    autoPrefilledStudioCount: false,
                                  },
                                }))
                              }}
                              disabled={isPast || saving}
                              placeholder="Не задано"
                              className="input w-full"
                            />
                            <div className="text-[11px] text-slate-500">
                              Сейчас на курсе: {row.current_studio_count ?? 0} студ.
                            </div>
                          </label>

                          <label className="space-y-2">
                            <div className="text-xs font-medium text-slate-700">Выручка на 1 студию, ₽</div>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={formatNumberWithSpaces(draft.plannedRevenuePerStudioRub)}
                              onChange={(event) => {
                                const value = parseFormattedNumber(event.target.value)
                                setDraftByKey((prev) => ({
                                  ...prev,
                                  [rowKey]: {
                                    ...prev[rowKey],
                                    plannedRevenuePerStudioRub: value,
                                  },
                                }))
                              }}
                              disabled={isPast || saving}
                              placeholder="Не задано"
                              className="input w-full"
                            />
                            <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                              <span className="group relative inline-flex cursor-help text-slate-400">
                                <span className="sr-only">{previousPeriodHintTitle}</span>
                                <Info className="h-3.5 w-3.5" />
                                <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-72 -translate-x-1/2 rounded-lg border border-slate-200 bg-card px-3 py-2 text-left text-xs leading-5 text-slate-700 shadow-lg group-hover:block group-focus-within:block">
                                  <span className="block font-semibold text-slate-900">Факт</span>
                                  <span className="block">Среднее недельное значение на 1 студию за прошлый период по данным листка.</span>
                                  <span className="mt-2 block font-semibold text-slate-900">План</span>
                                  <span className="block">Среднее недельное значение ручного плана на 1 студию за прошлый период.</span>
                                </span>
                              </span>
                              <span>
                                Факт/план прошлого периода:{' '}
                                {formatHintValue(row.previous_period_fact_revenue_per_studio_rub)}
                                {' / '}
                                {formatHintValue(row.previous_period_planned_revenue_per_studio_rub)}
                              </span>
                            </div>
                          </label>

                          <div className="rounded-xl bg-slate-50 px-4 py-3">
                            <div className="text-[11px] uppercase tracking-wide text-slate-500">
                              Итог по курсу
                            </div>
                            <div className="mt-2 text-lg font-semibold text-slate-950">
                              {plannedRevenue === null ? '—' : `${formatCurrencyValue(plannedRevenue)} ₽`}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            disabled={saving}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background transition-colors hover:bg-foreground/90 disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
